import type { RegisterOptions } from 'react-hook-form';
import type {
  FieldItem,
  FormValues,
  NormalizedField,
  OnboardingFieldRow,
  RawField,
} from '@/features/auth/types/onboardingFields';
import { KYC } from '@/features/auth/types/onboardingFields';

export const parseJSON = <T,>(value: string | null, fallback: T): T => {
  try {
    return (JSON.parse(value || 'null') as T) ?? fallback;
  } catch {
    return fallback;
  }
};

export const fieldKey = (name = '') => name.toLowerCase();

export const hasValue = (value: unknown) =>
  typeof value === 'string' ? value.trim().length > 0 : Boolean(value);

export const optionValue = (opt: unknown) => {
  if (opt == null) return '';
  if (typeof opt === 'string' || typeof opt === 'number') return String(opt);
  if (typeof opt === 'object') {
    const o = opt as {
      value?: unknown;
      key?: unknown;
      label?: unknown;
      name?: unknown;
    };
    return String(o.value ?? o.key ?? o.label ?? o.name ?? '');
  }
  return String(opt);
};

export const optionLabel = (opt: unknown) => {
  if (opt == null) return '';
  if (typeof opt === 'string' || typeof opt === 'number') return String(opt);
  if (typeof opt === 'object') {
    const o = opt as { label?: unknown; name?: unknown; value?: unknown };
    return String(o.label ?? o.name ?? o.value ?? '');
  }
  return String(opt);
};

export const isSelectType = (fieldType: string, options: unknown[]) =>
  options.length > 0 ||
  ['select', 'dropdown', 'enum'].includes(fieldType.toLowerCase());

export const normalizeFieldItem = (item: FieldItem): NormalizedField | null => {
  const field = item?.fieldId || item?.field;
  if (!field?.fieldName) return null;

  return {
    fieldName: field.fieldName,
    fieldLabel: field.fieldLabel || field.fieldName,
    fieldType: field.fieldType || 'text',
    placeholder: field.placeholder || '',
    options: field.options || [],
    validation: field.validation,
    isOnboarding:
      item?.isOnboarding === false || field.isOnboarding === false
        ? false
        : true,
    isRequired: item?.required === true,
    visible: item?.visible !== false,
  };
};

export const findCompanyFieldName = (selectedFields: FieldItem[] = []) =>
  selectedFields
    .map(normalizeFieldItem)
    .find((f) => f?.isOnboarding && fieldKey(f.fieldName) === KYC.COMPANY)
    ?.fieldName;

export function buildValidationRules(
  fieldLabel: string,
  fieldType: string,
  validation: RawField['validation'],
  isRequired: boolean,
): RegisterOptions {
  const rules: RegisterOptions = {};
  const isDate = fieldType === 'date';

  if (isRequired) rules.required = `${fieldLabel} is required`;
  if (!validation) return rules;

  if (!isDate) {
    const min = validation.minLength ?? validation.min;
    const max = validation.maxLength ?? validation.max;
    if (min != null) {
      rules.minLength = {
        value: Number(min),
        message: `${fieldLabel} must be at least ${min} characters`,
      };
    }
    if (max != null) {
      rules.maxLength = {
        value: Number(max),
        message: `${fieldLabel} must be at most ${max} characters`,
      };
    }
  }

  if (validation.pattern) {
    try {
      rules.pattern = {
        value: new RegExp(validation.pattern),
        message: `Invalid ${fieldLabel} format`,
      };
    } catch {
      /* ignore bad pattern */
    }
  }

  return rules;
}

export function buildOnboardingFields(
  selectedFields: FieldItem[],
): OnboardingFieldRow[] {
  return selectedFields
    .map((item, index) => ({ item, field: normalizeFieldItem(item), index }))
    .filter(
      (
        row,
      ): row is OnboardingFieldRow =>
        Boolean(row.field?.visible && row.field.isOnboarding),
    );
}

export function syncFormCacheWithApiFields(
  storageKey: string,
  apiFieldNames: string[],
): FormValues {
  const allowed = new Set([...apiFieldNames, 'mediaId']);
  const cached = parseJSON<FormValues>(sessionStorage.getItem(storageKey), {});
  const next: FormValues = {};

  Object.entries(cached).forEach(([key, value]) => {
    if (allowed.has(key)) next[key] = value;
  });
  apiFieldNames.forEach((name) => {
    if (!(name in next)) next[name] = '';
  });

  sessionStorage.setItem(storageKey, JSON.stringify(next));
  return next;
}

export function cleanVerifiedValuesForFields(
  verifiedStorageKey: string,
  allowed: Set<string>,
  prev: Record<string, string>,
): Record<string, string> {
  const cleaned: Record<string, string> = {};
  Object.entries(prev).forEach(([key, value]) => {
    if (allowed.has(key)) cleaned[key] = value;
  });
  sessionStorage.setItem(verifiedStorageKey, JSON.stringify(cleaned));
  return cleaned;
}

export function getUserDetailStorageKeys(userMobile: string) {
  return {
    storageKey: `user_detail_form_${userMobile}`,
    verifiedStorageKey: `user_detail_verified_${userMobile}`,
    detailsStorageKey: `user_detail_kyc_${userMobile}`,
  };
}
